import { defineModule, definePermission } from '@shared/contract'

// Auswertungen: die Kennzahlen der Startseite und die Berichte, nach denen ein Handelsbetrieb steuert.
// Das Modul liest nur. Jede Zahl ist aus Belegen bzw. dem Bewegungsjournal abgeleitet und führt dorthin zurück;
// die verdichteten Tabellen (sales_daily, stock_daily_snapshot) sind ein Zwischenspeicher, nie eine eigene Wahrheit.
//
// Rechte (db/seeds/demo/roles.ts, Abschnitt „Auswertungen“) – alle je Standort vergebbar, außer dem Neuaufbau:
//   * dashboard.read   Kennzahlen der Startseite. Welche Kacheln erscheinen, folgt den übrigen Rechten der Person.
//   * sales.read       Umsatz, Renner und Penner, Kassenauswertung.
//   * stock.read       Lagerwert, Reichweite, Ladenhüter, MHD-Risiko, Schwund.
//   * purchasing.read  Einkaufsvolumen, Preisentwicklung, Liefertreue.
//   * finance.read     Altersstruktur der Forderungen, Zahlungseingänge, Pfandverbindlichkeiten.
//   * data.rebuild     verdichtete Daten für einen Zeitraum neu aufbauen (Verwaltung).
// Roherträge, Einstandswerte und Lagerwerte verlangen zusätzlich `costs.read` – ohne das Recht fehlen sie in jeder
// Ausgabe (Bildschirm, CSV, Assistent), nicht nur in der Anzeige.
const GROUP = 'Auswertungen'

export default defineModule({
  id: 'reporting',
  title: 'Auswertungen',
  description: 'Kennzahlen der Startseite und Berichte zu Umsatz, Rohertrag, Bestand, Schwund, Einkauf und Forderungen – jede Zahl mit dem Weg zu ihren Belegen.',
  // die Quellen: Belege und Journal des Kerns, gelesen über die Module, die sie führen. Kasse und Pfand sind
  // Branchenmodule – ohne sie bleiben die Auswertungen aktiv, ihre Kacheln und Berichte entfallen.
  dependsOn: ['platform', 'masterdata', 'inventory', 'pricing', 'purchasing', 'sales', 'finance', 'stocktaking'],
  optionalDependsOn: ['pos', 'deposits'],
  permissions: [
    definePermission({
      key: 'reporting.dashboard.read',
      label: 'Kennzahlen der Startseite sehen',
      group: GROUP,
      description: 'Die Kacheln der Startseite. Jede Kachel verlangt zusätzlich das Recht ihrer Auswertung am jeweiligen Standort.',
    }),
    definePermission({
      key: 'reporting.sales.read',
      label: 'Umsatzauswertungen lesen',
      group: GROUP,
      description: 'Umsatz nach Zeitraum, Standort, Vertriebsweg, Warengruppe, Artikel und Kunde; Renner und Penner; Kassenauswertung.',
    }),
    definePermission({
      key: 'reporting.stock.read',
      label: 'Bestandsauswertungen lesen',
      group: GROUP,
      description: 'Bestand nach Standort und Warengruppe, Reichweite, Ladenhüter, Verfallsrisiko, Schwund. Werte nur mit dem Recht „Einstandspreise sehen“.',
    }),
    definePermission({
      key: 'reporting.purchasing.read',
      label: 'Einkaufsauswertungen lesen',
      group: GROUP,
      description: 'Einkaufsvolumen je Lieferant, Preisentwicklung je Artikel, Liefertreue.',
    }),
    definePermission({
      key: 'reporting.finance.read',
      label: 'Finanzauswertungen lesen',
      group: GROUP,
      description: 'Altersstruktur der Forderungen, Zahlungsziel in Tagen, Umsatz gegen Zahlungseingang, Verbindlichkeiten aus Rückgabeansprüchen.',
    }),
    definePermission({
      key: 'reporting.data.rebuild',
      label: 'Auswertungsdaten neu aufbauen',
      group: GROUP,
      description: 'Baut die verdichteten Tageswerte eines Zeitraums aus Belegen und Bewegungsjournal neu auf. Ändert keine Buchung.',
      locationScoped: false,
    }),
  ],
})
